import { jsx as _jsx } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ChatListView from '../views/ChatListView';
import ChatConversationView from '../views/ChatConversationView';
import CreateChatViewContainer from '../containers/CreateChatViewContainer';
import chatApiClient, { getConversations, getMessages } from '@edusense/people-chat/api/chatApiClient';
import RSocketChatClient from '../../services/rsocket/rsocket-client';
import { tokenManager } from '@edusense/shared-auth-core';
import { useDMListStore } from '../../stores';
const PeopleChatModal = ({ onSwitchToAIChat, onClose })=>{
    const [searchTerm, setSearchTerm] = useState('');
    const [isCreateChatMode, setIsCreateChatMode] = useState(false);
    const [selectedChatRoom, setSelectedChatRoom] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const rsocketClientRef = useRef(null);
    const connectionPromiseRef = useRef(null);
    const [rsocketClientState, setRsocketClientState] = useState(null);
    const [isRSocketConnecting, setIsRSocketConnecting] = useState(false);
    const messageSubscriptionRef = useRef(null);
    const roomInfoSubscriptionRef = useRef(null);
    const roomConnectionRef = useRef(null);
    const lastMarkedMessageRef = useRef({});
    const [resolvedUser, setResolvedUser] = useState(()=>({
            id: tokenManager.getUserId() ?? null,
            displayName: tokenManager.getUserDisplayName() ?? null
        }));
    useEffect(()=>{
        let cancelled = false;
        const syncFromTokenManager = ()=>{
            if (cancelled) {
                return;
            }
            const nextId = tokenManager.getUserId() ?? null;
            const nextDisplay = tokenManager.getUserDisplayName() ?? null;
            setResolvedUser((prev)=>{
                if (prev.id === nextId && prev.displayName === nextDisplay) {
                    return prev;
                }
                return {
                    id: nextId,
                    displayName: nextDisplay
                };
            });
        };
        syncFromTokenManager();
        const interval = window.setInterval(syncFromTokenManager, 2000);
        return ()=>{
            cancelled = true;
            window.clearInterval(interval);
        };
    }, []);
    const currentUserId = resolvedUser.id;
    const currentUserDisplayName = resolvedUser.displayName;
    const fallbackUserId = currentUserId ?? 'unknown-user';
    const { setDMLIst } = useDMListStore();
    const normalizeParticipant = useCallback((participant)=>{
        const rawId = participant?.userId ?? participant?.id ?? participant?.user?.id;
        const id = rawId != null ? String(rawId) : undefined;
        const displayNameCandidates = [
            participant?.displayName,
            participant?.name,
            participant?.userName,
            participant?.user?.displayName,
            participant?.user?.name,
            participant?.user?.userName
        ].filter((value)=>typeof value === 'string' && value.trim().length > 0);
        const displayName = displayNameCandidates[0] ?? (id && id !== fallbackUserId ? id : '알 수 없는 사용자');
        const userNameCandidates = [
            participant?.userName,
            participant?.user?.userName,
            participant?.name,
            participant?.user?.name
        ].filter((value)=>typeof value === 'string' && value.trim().length > 0);
        const userName = userNameCandidates[0] ?? undefined;
        const avatarCandidates = [
            participant?.avatarImageLink,
            participant?.avatarUrl,
            participant?.profileImageUrl,
            participant?.user?.avatarImageLink,
            participant?.user?.avatarUrl,
            participant?.user?.profileImageUrl
        ].filter(Boolean);
        const isCurrentUser = currentUserId ? id != null && String(id) === String(currentUserId) : Boolean(participant?.isCurrentUser);
        return {
            ...participant,
            id: id ?? participant?.id ?? participant?.userId,
            userId: id,
            displayName,
            userName: userName ?? participant?.userName ?? participant?.user?.userName ?? null,
            avatarUrl: avatarCandidates[0] ?? participant?.avatarUrl ?? null,
            isCurrentUser
        };
    }, [
        currentUserId,
        fallbackUserId
    ]);
    const buildParticipantLookup = useCallback((chat)=>{
        const participants = Array.isArray(chat?.participants) ? chat.participants.map(normalizeParticipant) : [];
        const lookup = new Map();
        participants.forEach((participant)=>{
            const candidateId = participant?.userId ?? participant?.id ?? participant?.user?.id;
            if (candidateId != null) {
                lookup.set(String(candidateId), participant);
            }
        });
        return lookup;
    }, [
        normalizeParticipant
    ]);
    const normalizeMessagesForChat = useCallback((rawMessages, baseChat)=>{
        const participantLookup = buildParticipantLookup(baseChat);
        return rawMessages.map((rawMessage)=>{
            const originalUser = rawMessage.user ?? {};
            const candidateUserId = originalUser.id ?? originalUser.userId ?? rawMessage.userId ?? rawMessage.senderId ?? rawMessage.user?.userId ?? rawMessage.user?.userID ?? rawMessage.user?.uid ?? null;
            const resolvedUserId = candidateUserId != null ? String(candidateUserId) : fallbackUserId;
            const participant = participantLookup.get(resolvedUserId);
            const displayNameCandidates = [
                originalUser.name,
                originalUser.userName,
                originalUser.displayName,
                participant?.displayName,
                participant?.name,
                participant?.userName
            ].filter((value)=>typeof value === 'string' && value.trim().length > 0);
            const resolvedDisplayName = displayNameCandidates[0] ?? (resolvedUserId !== 'unknown-user' ? resolvedUserId : '알 수 없는 사용자');
            const avatarCandidates = [
                originalUser.avatarImageLink,
                originalUser.avatarUrl,
                participant?.avatarImageLink,
                participant?.avatarUrl
            ].filter(Boolean);
            const messageId = rawMessage.id ?? rawMessage.messageId ?? rawMessage.messageUuid ?? `${resolvedUserId}-${rawMessage.sent ?? rawMessage.timestamp ?? Date.now()}`;
            const sentAt = rawMessage.sent ?? rawMessage.timestamp ?? new Date().toISOString();
            return {
                ...rawMessage,
                id: String(messageId),
                roomId: String(rawMessage.roomId ?? baseChat?.id ?? baseChat?.chatRoomId ?? ''),
                content: rawMessage.content ?? rawMessage.text ?? '',
                userId: resolvedUserId,
                user: {
                    id: resolvedUserId,
                    name: resolvedDisplayName,
                    userName: originalUser.userName ?? originalUser.name ?? participant?.userName ?? resolvedDisplayName,
                    avatarImageLink: avatarCandidates[0] ?? null
                },
                sent: sentAt,
                status: rawMessage.status
            };
        });
    }, [
        buildParticipantLookup,
        fallbackUserId
    ]);
    const normalizeMessageForChat = useCallback((rawMessage, baseChat)=>normalizeMessagesForChat([
            rawMessage
        ], baseChat)[0], [
        normalizeMessagesForChat
    ]);
    const deriveConversationName = useCallback((chat, participants)=>{
        if (!chat?.isGroupThread) {
            const otherParticipant = participants.find((participant)=>{
                const id = participant?.userId ?? participant?.id;
                if (!id) return false;
                return currentUserId ? String(id) !== String(currentUserId) : true;
            });
            if (otherParticipant?.displayName) {
                const display = otherParticipant.displayName.trim();
                const handle = (otherParticipant.userName ?? '').trim();
                if (display && handle && display !== handle) {
                    return `${display}@${handle}`;
                }
                return display || handle || `채팅방 ${chat?.id ?? chat?.chatRoomId ?? ''}`;
            }
        }
        const nameCandidates = [
            chat?.roomName,
            chat?.chatRoomName,
            chat?.groupName
        ].filter((value)=>typeof value === 'string' && value.trim().length > 0);
        if (nameCandidates.length > 0) {
            return nameCandidates[0];
        }
        if (chat?.isGroupThread && participants.length > 0) {
            return participants.map((participant)=>participant.displayName).filter(Boolean).join(', ');
        }
        return `채팅방 ${chat?.id ?? chat?.chatRoomId ?? ''}`;
    }, [
        currentUserId
    ]);
    const convertConversationToRoomInfo = useCallback((conversation)=>{
        if (!conversation) {
            return null;
        }
        const participants = Array.isArray(conversation?.participants) ? conversation.participants : [];
        const roomId = String(conversation?.id ?? conversation?.chatRoomId ?? conversation?.roomId ?? '');
        if (!roomId) {
            return null;
        }
        const roomName = deriveConversationName(conversation, participants);
        const otherParticipant = participants.find((participant)=>!participant?.isCurrentUser) ?? participants[0] ?? null;
        const normalizedLastMessage = (()=>{
            const raw = conversation?.lastMessage;
            if (typeof raw === 'string') {
                return raw;
            }
            if (raw && typeof raw === 'object') {
                return raw.text ?? '';
            }
            return '';
        })();
        const normalizedLastMessageTime = (()=>{
            const raw = conversation?.lastMessage;
            if (typeof raw === 'string') {
                return conversation?.lastMessageTime ?? null;
            }
            if (raw && typeof raw === 'object') {
                return raw.timestamp ?? conversation?.lastMessageTime ?? null;
            }
            return conversation?.lastMessageTime ?? null;
        })();
        return {
            roomId,
            lastMessage: normalizedLastMessage,
            lastMessageTime: normalizedLastMessageTime ?? undefined,
            unreadMessageCount: conversation?.unreadMessageCount ?? 0,
            roomName,
            userId: otherParticipant?.userId ?? otherParticipant?.id ?? '',
            participants,
            isGroupThread: Boolean(conversation?.isGroupThread),
            currentUserStatus: conversation?.currentUserStatus
        };
    }, [
        deriveConversationName
    ]);
    const syncDMListStore = useCallback((nextConversations)=>{
        const mapped = {};
        nextConversations.forEach((conversation)=>{
            const roomInfo = convertConversationToRoomInfo(conversation);
            if (roomInfo) {
                mapped[roomInfo.roomId] = roomInfo;
            }
        });
        setDMLIst(mapped);
    }, [
        convertConversationToRoomInfo,
        setDMLIst
    ]);
    const updateConversationMetadata = useCallback((roomId, updater)=>{
        let computed = null;
        setConversations((prevConversations)=>{
            let modified = false;
            const updatedList = prevConversations.map((conversation)=>{
                const candidateId = String(conversation?.id ?? conversation?.chatRoomId ?? conversation?.roomId ?? '');
                if (candidateId !== roomId) {
                    return conversation;
                }
                const nextConversation = updater(conversation);
                if (nextConversation !== conversation) {
                    modified = true;
                }
                return nextConversation;
            });
            if (!modified) {
                return prevConversations;
            }
            const sorted = [
                ...updatedList
            ].sort((a, b)=>{
                const aTime = new Date(a?.lastMessageTime ?? 0).getTime();
                const bTime = new Date(b?.lastMessageTime ?? 0).getTime();
                return bTime - aTime;
            });
            computed = sorted;
            return sorted;
        });
        if (computed) {
            syncDMListStore(computed);
        }
    }, [
        syncDMListStore
    ]);
    const markThreadAsRead = useCallback(async (roomId, conversation)=>{
        const resolvedRoomId = String(roomId ?? conversation?.id ?? conversation?.chatRoomId ?? conversation?.roomId ?? '').trim();
        if (!resolvedRoomId) {
            return;
        }
        const messages = Array.isArray(conversation?.messages) ? conversation.messages : [];
        let lastMessageIdentifier = null;
        for(let index = messages.length - 1; index >= 0; index -= 1){
            const candidate = messages[index]?.id ?? messages[index]?.messageId ?? messages[index]?.messageUuid ?? null;
            if (candidate == null) {
                continue;
            }
            lastMessageIdentifier = String(candidate);
            if (lastMessageIdentifier.length > 0) {
                break;
            }
        }
        const effectiveMessageId = lastMessageIdentifier && lastMessageIdentifier.length > 0 ? lastMessageIdentifier : '0';
        if (lastMarkedMessageRef.current[resolvedRoomId] === effectiveMessageId) {
            return;
        }
        try {
            await chatApiClient.markMessageAsRead(resolvedRoomId, effectiveMessageId);
            lastMarkedMessageRef.current[resolvedRoomId] = effectiveMessageId;
            updateConversationMetadata(resolvedRoomId, (prev)=>({
                    ...prev,
                    unreadMessageCount: 0
                }));
        } catch (error) {
            console.warn('[PeopleChatModal] Failed to mark thread as read:', error);
        }
    }, [
        updateConversationMetadata
    ]);
    const normalizeConversation = useCallback((rawChat)=>{
        if (!rawChat) {
            return rawChat;
        }
        const participants = Array.isArray(rawChat?.participants) ? rawChat.participants.map(normalizeParticipant) : [];
        const roomName = deriveConversationName(rawChat, participants);
        return {
            ...rawChat,
            participants,
            roomName
        };
    }, [
        deriveConversationName,
        normalizeParticipant
    ]);
    const loadConversations = useCallback(async (options = {})=>{
        const { showSpinner = true } = options;
        try {
            if (showSpinner) {
                setIsLoading(true);
            }
            setError(null);
            const data = await getConversations();
            const normalized = Array.isArray(data) ? data.map((conversation)=>normalizeConversation(conversation)).sort((a, b)=>{
                const aTime = new Date(a?.lastMessageTime ?? 0).getTime();
                const bTime = new Date(b?.lastMessageTime ?? 0).getTime();
                return bTime - aTime;
            }) : [];
            setConversations(normalized);
            syncDMListStore(normalized);
        } catch (err) {
            console.error('Failed to load conversations:', err);
            setError('대화 목록을 불러올 수 없습니다.');
        } finally{
            if (showSpinner) {
                setIsLoading(false);
            }
        }
    }, [
        normalizeConversation,
        syncDMListStore
    ]);
    const applyRoomInfoUpdate = useCallback((roomInfos)=>{
        if (!roomInfos) {
            return;
        }
        let newRoomAdded = false;
        let needsFullRefresh = false;
        let computed = null;
        setConversations((prevConversations)=>{
            const conversationMap = new Map();
            prevConversations.forEach((conversation)=>{
                const key = String(conversation?.id ?? conversation?.chatRoomId ?? conversation?.roomId ?? '');
                if (key) {
                    conversationMap.set(key, conversation);
                }
            });
            Object.values(roomInfos).forEach((info)=>{
                if (!info) {
                    return;
                }
                const roomId = String(info.roomId ?? '');
                if (!roomId) {
                    return;
                }
                const existing = conversationMap.get(roomId);
                if (existing) {
                    const participants = Array.isArray(existing?.participants) ? existing.participants : [];
                    const recomputedName = deriveConversationName(existing, participants);
                    const hasInfoMessage = typeof info.lastMessage === 'string' && info.lastMessage.trim().length > 0;
                    const existingUnread = typeof existing.unreadMessageCount === 'number' ? existing.unreadMessageCount : 0;
                    const infoProvidesUnread = typeof info.unreadMessageCount === 'number';
                    const isRoomCurrentlyOpen = selectedChatRoom ? String(selectedChatRoom.id ?? selectedChatRoom.chatRoomId ?? '') === roomId : false;
                    const isNewMessage = hasInfoMessage && typeof existing.lastMessage === 'string' && existing.lastMessage !== info.lastMessage;
                    let nextUnreadCount = existingUnread;
                    if (infoProvidesUnread) {
                        nextUnreadCount = info.unreadMessageCount ?? existingUnread;
                    } else if (isNewMessage && !isRoomCurrentlyOpen) {
                        nextUnreadCount = existingUnread + 1;
                    } else if (isRoomCurrentlyOpen) {
                        nextUnreadCount = 0;
                    }
                    conversationMap.set(roomId, {
                        ...existing,
                        roomName: recomputedName || info.roomName || existing.roomName,
                        lastMessage: hasInfoMessage ? info.lastMessage : existing.lastMessage,
                        lastMessageTime: info.lastMessageTime ?? existing.lastMessageTime,
                        unreadMessageCount: nextUnreadCount
                    });
                    if (!hasInfoMessage) {
                        needsFullRefresh = true;
                    }
                } else {
                    const hasInfoMessage = typeof info.lastMessage === 'string' && info.lastMessage.trim().length > 0;
                    conversationMap.set(roomId, {
                        id: roomId,
                        chatRoomId: roomId,
                        roomId,
                        roomName: info.roomName,
                        lastMessage: hasInfoMessage ? info.lastMessage : '',
                        lastMessageTime: info.lastMessageTime,
                        unreadMessageCount: info.unreadMessageCount ?? 0,
                        participants: [],
                        isGroupThread: false
                    });
                    newRoomAdded = true;
                    if (!hasInfoMessage) {
                        needsFullRefresh = true;
                    }
                }
            });
            const merged = Array.from(conversationMap.values());
            merged.sort((a, b)=>{
                const aTime = new Date(a?.lastMessageTime ?? 0).getTime();
                const bTime = new Date(b?.lastMessageTime ?? 0).getTime();
                return bTime - aTime;
            });
            computed = merged;
            return merged;
        });
        if (newRoomAdded || needsFullRefresh) {
            loadConversations({
                showSpinner: false
            }).catch((error)=>{
                console.warn('[PeopleChatModal] Background conversation refresh failed:', error);
            });
            return;
        }
        if (computed) {
            syncDMListStore(computed);
        }
    }, [
        deriveConversationName,
        loadConversations,
        selectedChatRoom?.chatRoomId,
        selectedChatRoom?.id,
        syncDMListStore
    ]);
    const fetchMessagesForRoom = useCallback(async (roomId, baseChat)=>{
        const messagesResponse = await getMessages(roomId);
        const rawMessages = messagesResponse.data?.content || messagesResponse.data || [];
        const participants = Array.isArray(baseChat?.participants) ? baseChat.participants : [];
        const enrichedChat = {
            ...baseChat,
            id: roomId,
            participants
        };
        const normalizedMessages = normalizeMessagesForChat(rawMessages, enrichedChat);
        return {
            ...enrichedChat,
            messages: normalizedMessages
        };
    }, [
        normalizeMessagesForChat
    ]);
    const tearDownRSocket = useCallback(()=>{
        if (messageSubscriptionRef.current) {
            try {
                messageSubscriptionRef.current();
            } catch (error) {
                console.warn('[PeopleChatModal] Failed to dispose message subscription', error);
            }
            messageSubscriptionRef.current = null;
        }
        if (roomInfoSubscriptionRef.current) {
            try {
                roomInfoSubscriptionRef.current();
            } catch (error) {
                console.warn('[PeopleChatModal] Failed to dispose room-info subscription', error);
            }
            roomInfoSubscriptionRef.current = null;
        }
        if (roomConnectionRef.current) {
            try {
                roomConnectionRef.current();
            } catch (error) {
                console.warn('[PeopleChatModal] Failed to dispose room connection subscription', error);
            }
            roomConnectionRef.current = null;
        }
        if (rsocketClientRef.current) {
            console.log('[PeopleChatModal] 🔌 Disconnecting RSocket client');
            try {
                rsocketClientRef.current.disconnect();
            } catch (error) {
                console.warn('[PeopleChatModal] Error while disconnecting RSocket client', error);
            }
            rsocketClientRef.current = null;
        }
        connectionPromiseRef.current = null;
        setRsocketClientState(null);
        setIsRSocketConnecting(false);
    }, []);
    const attachRoomInfoSubscription = useCallback((client)=>{
        if (!client) {
            return;
        }
        if (roomInfoSubscriptionRef.current) {
            try {
                roomInfoSubscriptionRef.current();
            } catch (error) {
                console.warn('[PeopleChatModal] Failed to dispose previous room-info subscription', error);
            }
            roomInfoSubscriptionRef.current = null;
        }
        const roomInfoUserId = currentUserId ?? tokenManager.getUserId();
        if (!roomInfoUserId) {
            return;
        }
        try {
            roomInfoSubscriptionRef.current = client.subscribeToRoomInfos(String(roomInfoUserId), applyRoomInfoUpdate);
        } catch (error) {
            console.error('[PeopleChatModal] Failed to subscribe to room infos:', error);
        }
    }, [
        applyRoomInfoUpdate,
        currentUserId
    ]);
    const ensureRSocketConnection = useCallback(async ()=>{
        if (rsocketClientRef.current) {
            return rsocketClientRef.current;
        }
        if (connectionPromiseRef.current) {
            return connectionPromiseRef.current;
        }
        const token = tokenManager.getToken();
        if (!token) {
            console.error('[PeopleChatModal] ❌ No auth token available for RSocket');
            setError('인증 토큰이 없습니다. 다시 로그인해주세요.');
            return null;
        }
        const isSecure = typeof window !== 'undefined' ? window.location.protocol === 'https:' : true;
        const wsUrl = isSecure ? 'wss://chat.ugot.uk/rsocket' : 'ws://localhost:8080/rsocket';
        console.log('[PeopleChatModal] 🔌 Connecting to RSocket:', wsUrl);
        setIsRSocketConnecting(true);
        const promise = (async ()=>{
            try {
                const client = new RSocketChatClient(wsUrl, token);
                await client.connect();
                rsocketClientRef.current = client;
                setRsocketClientState(client);
                setError(null);
                console.log('[PeopleChatModal] ✅ RSocket client connected successfully');
                attachRoomInfoSubscription(client);
                return client;
            } catch (error) {
                console.error('[PeopleChatModal] ❌ Failed to initialize RSocket:', error);
                setError('실시간 채팅 연결에 실패했습니다.');
                return null;
            } finally{
                setIsRSocketConnecting(false);
                connectionPromiseRef.current = null;
            }
        })();
        connectionPromiseRef.current = promise;
        return promise;
    }, [
        attachRoomInfoSubscription,
        setError
    ]);
    // RSocket 클라이언트 초기화 및 정리
    useEffect(()=>{
        let cancelled = false;
        ensureRSocketConnection().catch((error)=>{
            if (!cancelled) {
                console.error('[PeopleChatModal] ❌ RSocket initialization failed:', error);
            }
        });
        return ()=>{
            cancelled = true;
            tearDownRSocket();
        };
    }, [
        ensureRSocketConnection,
        tearDownRSocket
    ]);
    // 사용자 정보가 업데이트되면 룸 정보 구독을 재연결
    useEffect(()=>{
        attachRoomInfoSubscription(rsocketClientRef.current);
    }, [
        attachRoomInfoSubscription
    ]);
    // 토큰/사용자 정보가 늦게 동기화되는 경우를 대비해 재연결 시도
    useEffect(()=>{
        if (rsocketClientRef.current || isRSocketConnecting) {
            return;
        }
        void ensureRSocketConnection();
    }, [
        ensureRSocketConnection,
        isRSocketConnecting,
        currentUserId
    ]);
    // 채팅방 선택 시 메시지 구독
    useEffect(()=>{
        let cancelled = false;
        const subscribeToRoomMessages = async ()=>{
            if (!selectedChatRoom) {
                if (messageSubscriptionRef.current) {
                    try {
                        messageSubscriptionRef.current();
                    } catch (error) {
                        console.warn('[PeopleChatModal] Failed to dispose previous message subscription', error);
                    }
                    messageSubscriptionRef.current = null;
                }
                return;
            }
            const client = rsocketClientRef.current ?? await ensureRSocketConnection();
            if (!client || cancelled) {
                return;
            }
            const roomId = selectedChatRoom.id || selectedChatRoom.chatRoomId;
            if (!roomId) {
                return;
            }
            if (messageSubscriptionRef.current) {
                try {
                    messageSubscriptionRef.current();
                } catch (error) {
                    console.warn('[PeopleChatModal] Failed to dispose previous message subscription', error);
                }
                messageSubscriptionRef.current = null;
            }
            try {
                const unsubscribe = client.subscribeToMessages(String(roomId), (message)=>{
                    console.log('[PeopleChatModal] Received message:', message);
                    setSelectedChatRoom((prev)=>{
                        if (!prev) {
                            return prev;
                        }
                        const existingMessages = Array.isArray(prev.messages) ? prev.messages : [];
                        const normalisedIncoming = normalizeMessageForChat(message, prev);
                        const dedupedMessages = existingMessages.filter((existing)=>{
                            if (existing?.status !== 'sending') {
                                return true;
                            }
                            const existingContent = existing?.content || existing?.text;
                            const incomingContent = normalisedIncoming?.content || normalisedIncoming?.text;
                            const existingUserId = existing?.user?.id || existing?.sender?.id;
                            const incomingUserId = normalisedIncoming?.user?.id || normalisedIncoming?.sender?.id;
                            const isSameOptimistic = existingContent === incomingContent && existingUserId === incomingUserId;
                            return !isSameOptimistic;
                        });
                        return {
                            ...prev,
                            messages: [
                                ...dedupedMessages,
                                normalisedIncoming
                            ]
                        };
                    });
                    const normalizedContent = message.content ?? message?.text ?? '';
                    const normalizedTimestamp = message.sent ?? new Date().toISOString();
                    updateConversationMetadata(String(roomId), (conversation)=>{
                        const participants = Array.isArray(conversation?.participants) ? conversation.participants : [];
                        const recomputedName = deriveConversationName(conversation, participants);
                        return {
                            ...conversation,
                            roomName: recomputedName || conversation.roomName,
                            lastMessage: normalizedContent,
                            lastMessageTime: normalizedTimestamp,
                            unreadMessageCount: 0
                        };
                    });
                });
                messageSubscriptionRef.current = ()=>{
                    try {
                        unsubscribe();
                    } catch (error) {
                        console.warn('[PeopleChatModal] Failed to cancel message subscription', error);
                    }
                };
            } catch (error) {
                console.error('[PeopleChatModal] Failed to subscribe to messages:', error);
            }
        };
        subscribeToRoomMessages();
        return ()=>{
            cancelled = true;
            if (messageSubscriptionRef.current) {
                try {
                    messageSubscriptionRef.current();
                } catch (error) {
                    console.warn('[PeopleChatModal] Failed to dispose message subscription during cleanup', error);
                }
                messageSubscriptionRef.current = null;
            }
        };
    }, [
        selectedChatRoom,
        normalizeMessageForChat,
        updateConversationMetadata,
        deriveConversationName,
        ensureRSocketConnection
    ]);
    useEffect(()=>{
        const roomId = selectedChatRoom?.id ?? selectedChatRoom?.chatRoomId;
        if (!roomId) {
            if (!selectedChatRoom && roomConnectionRef.current) {
                try {
                    roomConnectionRef.current();
                } catch (error) {
                    console.warn('[PeopleChatModal] Failed to dispose room connection', error);
                }
                roomConnectionRef.current = null;
            }
            return;
        }
        let cancelled = false;
        (async ()=>{
            try {
                const client = rsocketClientRef.current ?? await ensureRSocketConnection();
                if (!client || cancelled) {
                    return;
                }
                roomConnectionRef.current?.();
                const cleanup = await client.connectToRoom(String(roomId));
                if (cancelled) {
                    cleanup();
                    return;
                }
                roomConnectionRef.current = cleanup;
            } catch (error) {
                console.error('[PeopleChatModal] Failed to establish room presence connection:', error);
            }
        })();
        return ()=>{
            cancelled = true;
        };
    }, [
        selectedChatRoom?.id,
        selectedChatRoom?.chatRoomId,
        ensureRSocketConnection,
        selectedChatRoom
    ]);
    useEffect(()=>{
        if (!selectedChatRoom) return;
        const roomId = selectedChatRoom.id || selectedChatRoom.chatRoomId;
        if (!roomId) return;
        let cancelled = false;
        const syncMessages = async ()=>{
            try {
                const refreshed = await fetchMessagesForRoom(roomId, selectedChatRoom);
                if (cancelled) return;
                setSelectedChatRoom((prev)=>{
                    if (!prev) return prev;
                    const prevRoomId = prev.id || prev.chatRoomId;
                    if (String(prevRoomId) !== String(roomId)) {
                        return prev;
                    }
                    return {
                        ...prev,
                        messages: refreshed.messages
                    };
                });
            } catch (err) {
                console.warn('[PeopleChatModal] periodic refresh failed:', err);
            }
        };
        const interval = window.setInterval(syncMessages, 2000);
        return ()=>{
            cancelled = true;
            window.clearInterval(interval);
        };
    }, [
        selectedChatRoom,
        fetchMessagesForRoom
    ]);
    useEffect(()=>{
        loadConversations({
            showSpinner: true
        });
    }, [
        loadConversations
    ]);
    useEffect(()=>{
        if (selectedChatRoom) {
            return;
        }
        let cancelled = false;
        const refresh = async ()=>{
            try {
                await loadConversations({
                    showSpinner: false
                });
            } catch (error) {
                console.warn('[PeopleChatModal] Background conversation refresh (list view) failed:', error);
            }
        };
        const intervalId = window.setInterval(()=>{
            if (!cancelled) {
                void refresh();
            }
        }, 5000);
        return ()=>{
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [
        selectedChatRoom,
        loadConversations
    ]);
    const filteredChats = conversations.filter((chat)=>{
        if (!chat.participants) return false;
        return chat.participants.some((p)=>(p.name || p.displayName || '').toLowerCase().includes(searchTerm.toLowerCase())) || chat.lastMessage && chat.lastMessage.toLowerCase().includes(searchTerm.toLowerCase());
    });
    const handleChatItemClick = async (chat)=>{
        try {
            setIsLoading(true);
            const roomId = chat.id || chat.chatRoomId;
            const normalizedChat = normalizeConversation(chat);
            const updated = await fetchMessagesForRoom(roomId, normalizedChat);
            setSelectedChatRoom(updated);
            void ensureRSocketConnection();
            updateConversationMetadata(String(roomId), ()=>({
                    ...updated,
                    unreadMessageCount: 0
                }));
        } catch (err) {
            console.error('Failed to load messages:', err);
            setError('메시지를 불러올 수 없습니다.');
        } finally{
            setIsLoading(false);
        }
    };
    useEffect(()=>{
        if (!selectedChatRoom) {
            return;
        }
        const roomId = selectedChatRoom.id || selectedChatRoom.chatRoomId;
        if (!roomId) {
            return;
        }
        void markThreadAsRead(roomId, selectedChatRoom);
    }, [
        selectedChatRoom,
        markThreadAsRead
    ]);
    const handleBackToList = ()=>{
        if (roomConnectionRef.current) {
            roomConnectionRef.current();
            roomConnectionRef.current = null;
        }
        setSelectedChatRoom(null);
        loadConversations({
            showSpinner: false
        }).catch((error)=>{
            console.warn('[PeopleChatModal] Failed to refresh conversations after returning to list:', error);
        });
    };
    const determineContentType = (mime)=>{
        if (!mime) {
            return 'PLAIN';
        }
        if (mime.startsWith('image/')) {
            return 'IMAGE';
        }
        if (mime.startsWith('video/')) {
            return 'VIDEO';
        }
        return 'PLAIN';
    };
    const handleSendMessage = async ({ content, media })=>{
        try {
            if (!selectedChatRoom) {
                console.error('[PeopleChatModal] ❌ Cannot send message: No chat room selected');
                setError('채팅방을 선택해주세요.');
                return;
            }
            const client = rsocketClientRef.current ?? await ensureRSocketConnection();
            if (!client) {
                console.warn('[PeopleChatModal] ⚠️ RSocket client unavailable, falling back to REST-only send');
            }
            const roomId = selectedChatRoom.id || selectedChatRoom.chatRoomId;
            const userId = currentUserId ?? tokenManager.getUserId() ?? fallbackUserId;
            const displayName = currentUserDisplayName || userId;
            const userProfile = tokenManager.getUser();
            const avatarUrl = userProfile?.avatarImageLink || userProfile?.avatarUrl || userProfile?.profile?.avatarUrl || null;
            const trimmedContent = (content ?? '').trim();
            const fallbackText = media ? media.fileName || '파일을 전송했습니다.' : '';
            const resolvedContent = trimmedContent || fallbackText || '파일을 전송했습니다.';
            const resolvedContentType = determineContentType(media?.contentType);
            console.log('[PeopleChatModal] 📤 Sending message to room:', roomId, 'from user:', userId, media ? `with attachment ${media.fileName}` : '');
            const nowIso = new Date().toISOString();
            const messagePayload = {
                id: '',
                content: resolvedContent,
                user: {
                    id: userId,
                    displayName: displayName,
                    name: displayName,
                    userName: displayName,
                    avatarImageLink: avatarUrl
                },
                sent: nowIso,
                roomId: String(roomId),
                replyToMessageId: null,
                replyContent: null,
                contentType: resolvedContentType,
                mediaUrl: media?.mediaUrl ?? null,
                userId
            };
            const optimisticRaw = {
                ...messagePayload,
                id: `optimistic-${Date.now()}`,
                status: 'sending'
            };
            if (media?.mediaUrl) {
                optimisticRaw.mediaUrl = media.mediaUrl;
                optimisticRaw.attachment = {
                    fileName: media.fileName,
                    contentType: media.contentType,
                    size: media.size
                };
            }
            const optimisticNormalized = selectedChatRoom ? normalizeMessageForChat(optimisticRaw, selectedChatRoom) : {
                ...optimisticRaw,
                user: {
                    id: userId,
                    displayName: displayName,
                    name: displayName,
                    userName: displayName,
                    avatarImageLink: avatarUrl
                }
            };
            setSelectedChatRoom((prev)=>{
                if (!prev) {
                    return prev;
                }
                const existingMessages = Array.isArray(prev.messages) ? prev.messages : [];
                return {
                    ...prev,
                    messages: [
                        ...existingMessages,
                        optimisticNormalized
                    ]
                };
            });
            updateConversationMetadata(String(roomId), (conversation)=>{
                const participants = Array.isArray(conversation?.participants) ? conversation.participants : [];
                const recomputedName = deriveConversationName(conversation, participants);
                return {
                    ...conversation,
                    roomName: recomputedName || conversation.roomName,
                    lastMessage: resolvedContent,
                    lastMessageTime: optimisticNormalized.sent ?? nowIso,
                    unreadMessageCount: 0
                };
            });
            const resolvedRoomId = String(roomId ?? '').trim();
            if (!resolvedRoomId) {
                throw new Error('[PeopleChatModal] Cannot send message without a valid roomId');
            }
            // RSocket으로 메시지 전송 (실시간 구독자용)
            if (client) {
                try {
                    await client.sendMessage(resolvedRoomId, messagePayload);
                    console.log('[PeopleChatModal] ✅ Message sent via RSocket successfully');
                } catch (socketError) {
                    console.warn('[PeopleChatModal] ⚠️ RSocket send failed, continuing with REST fallback:', socketError);
                }
            }
            let restPersisted = false;
            try {
                await chatApiClient.sendMessage(resolvedRoomId, {
                    content: resolvedContent,
                    contentType: resolvedContentType,
                    mediaUrl: media?.mediaUrl
                });
                restPersisted = true;
                console.log('[PeopleChatModal] ✅ Message persisted via REST API');
            } catch (restError) {
                console.warn('[PeopleChatModal] REST send fallback failed:', restError);
            }
            if (restPersisted) {
                try {
                    await new Promise((resolve)=>setTimeout(resolve, 200));
                    const refreshed = await fetchMessagesForRoom(resolvedRoomId, {
                        ...selectedChatRoom,
                        chatRoomId: selectedChatRoom.chatRoomId || resolvedRoomId
                    });
                    setSelectedChatRoom(refreshed);
                    updateConversationMetadata(resolvedRoomId, ()=>refreshed);
                } catch (refreshError) {
                    console.warn('[PeopleChatModal] Failed to refresh messages after send:', refreshError);
                }
            }
        } catch (err) {
            console.error('[PeopleChatModal] ❌ Failed to send message:', err);
            setError('메시지 전송에 실패했습니다.');
        }
    };
    const handleChatCreated = async (chatThread)=>{
        try {
            setIsCreateChatMode(false);
            // chatThread를 conversation 형식으로 변환 (id, messages 필드 초기화)
            const chatAsConversation = normalizeConversation({
                ...chatThread,
                id: chatThread.chatRoomId,
                messages: chatThread.messages || [] // messages 배열 초기화
            });
            setConversations((prev)=>{
                const roomId = String(chatAsConversation?.id ?? chatAsConversation?.chatRoomId ?? '');
                if (!roomId) {
                    return prev;
                }
                const alreadyExists = prev.some((conversation)=>{
                    const candidateId = String(conversation?.id ?? conversation?.chatRoomId ?? conversation?.roomId ?? '');
                    return candidateId === roomId;
                });
                if (alreadyExists) {
                    return prev;
                }
                return [
                    chatAsConversation,
                    ...prev
                ];
            });
            // 바로 채팅창 열기 (대화 목록을 기다리지 않음)
            await handleChatItemClick(chatAsConversation);
            // 백그라운드에서 대화 목록 새로고침
            getConversations().then((updatedConversations)=>{
                const normalized = Array.isArray(updatedConversations) ? updatedConversations.map((conversation)=>normalizeConversation(conversation)).sort((a, b)=>{
                    const aTime = new Date(a?.lastMessageTime ?? 0).getTime();
                    const bTime = new Date(b?.lastMessageTime ?? 0).getTime();
                    return bTime - aTime;
                }) : [];
                setConversations(normalized);
            }).catch((err)=>{
                console.warn('Failed to refresh conversations list:', err);
            });
        } catch (err) {
            console.error('Failed to open chat:', err);
            setError('채팅방을 열 수 없습니다.');
        }
    };
    const handleBackFromCreateChat = ()=>{
        setIsCreateChatMode(false);
    };
    const handleDeleteThread = useCallback(async (roomId)=>{
        try {
            setError(null);
            const resolvedRoomId = String(roomId ?? '').trim();
            if (!resolvedRoomId) {
                throw new Error('[PeopleChatModal] handleDeleteThread requires a valid roomId');
            }
            await chatApiClient.deleteChatThread(resolvedRoomId);
            setConversations((prevConversations)=>prevConversations.filter((conversation)=>{
                    const candidateId = String(conversation?.id ?? conversation?.chatRoomId ?? conversation?.roomId ?? '');
                    return candidateId !== resolvedRoomId;
                }));
            setSelectedChatRoom((prev)=>{
                if (!prev) {
                    return prev;
                }
                const candidateId = String(prev?.id ?? prev?.chatRoomId ?? prev?.roomId ?? '');
                if (candidateId === resolvedRoomId) {
                    return null;
                }
                return prev;
            });
        } catch (err) {
            console.error('[PeopleChatModal] Failed to delete thread:', err);
            setError('채팅방을 나갈 수 없습니다. 다시 시도해주세요.');
        }
    }, []);
    // 새 채팅 만들기 화면
    if (isCreateChatMode) {
        return /*#__PURE__*/ _jsx(CreateChatViewContainer, {
            onBack: handleBackFromCreateChat,
            onSwitchToAIChat: onSwitchToAIChat,
            onChatCreated: handleChatCreated,
            onClose: onClose
        });
    }
    // 채팅 대화 화면
    if (selectedChatRoom) {
        return /*#__PURE__*/ _jsx(ChatConversationView, {
            chatRoom: selectedChatRoom,
            onBack: handleBackToList,
            onSwitchToAIChat: onSwitchToAIChat,
            onSendMessage: handleSendMessage,
            currentUserId: currentUserId,
            rsocketClient: rsocketClientState,
            ensureRSocketConnection: ensureRSocketConnection,
            isRSocketConnecting: isRSocketConnecting
        });
    }
    // 채팅 목록 화면
    return /*#__PURE__*/ _jsx(ChatListView, {
        chats: filteredChats,
        pendingRequests: [],
        searchTerm: searchTerm,
        onSearchChange: setSearchTerm,
        onChatItemClick: handleChatItemClick,
        onCreateChatClick: ()=>setIsCreateChatMode(true),
        onSwitchToAIChat: onSwitchToAIChat,
        isLoading: isLoading,
        onClose: onClose,
        onDeleteThread: handleDeleteThread
    });
};
export default PeopleChatModal;
